import { useRetainedRef, useRetainedState } from "@appretio/shared";
import { useCallback, useEffect } from "react";

/**
 * Runs ``open`` the first time a page mounts with ``ready`` true, once per
 * browser session. Coming back to the page skips it — its selections are
 * retained instead — and a reload starts over, on the defaults again.
 *
 * ``scope`` is the page's retained-state scope; undefined runs ``open``
 * once per mount instead. ``key`` names the opener, because a page has
 * several (its view, its context, its cube) and each becomes ready on its
 * own fetch.
 *
 * Returns ``markOpened``, which spends the opening without running
 * ``open`` — for a choice the user made before it was ready. It also works
 * after the page unmounts, for a save that lands after the user left.
 */
export function useOpenOnce(
  scope: string | undefined,
  key: string,
  ready: boolean,
  open: () => void
) {
  const [opened, setOpened] = useRetainedState(scope, `opened:${key}`, false);
  useEffect(() => {
    if (opened || !ready) return;
    setOpened(true);
    open();
  }, [opened, ready, open, setOpened]);
  // The same retained slot, written directly: a state update after unmount
  // never reaches the registry.
  const retainedOpened = useRetainedRef(scope, `opened:${key}`, false);
  return useCallback(() => {
    setOpened(true);
    retainedOpened.current = true;
  }, [setOpened, retainedOpened]);
}
